import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

export enum CTooltipPlacement {
  Top = 'top',
  TopStart = 'top-start',
  TopEnd = 'top-end',
  Bottom = 'bottom',
  BottomStart = 'bottom-start',
  BottomEnd = 'bottom-end',
  Left = 'left',
  LeftStart = 'left-start',
  LeftEnd = 'left-end',
  Right = 'right',
  RightStart = 'right-start',
  RightEnd = 'right-end',
}

const FLIP_MAP: Record<CTooltipPlacement, CTooltipPlacement> = {
  [CTooltipPlacement.Top]: CTooltipPlacement.Bottom,
  [CTooltipPlacement.TopStart]: CTooltipPlacement.BottomStart,
  [CTooltipPlacement.TopEnd]: CTooltipPlacement.BottomEnd,
  [CTooltipPlacement.Bottom]: CTooltipPlacement.Top,
  [CTooltipPlacement.BottomStart]: CTooltipPlacement.TopStart,
  [CTooltipPlacement.BottomEnd]: CTooltipPlacement.TopEnd,
  [CTooltipPlacement.Left]: CTooltipPlacement.Right,
  [CTooltipPlacement.LeftStart]: CTooltipPlacement.RightStart,
  [CTooltipPlacement.LeftEnd]: CTooltipPlacement.RightEnd,
  [CTooltipPlacement.Right]: CTooltipPlacement.Left,
  [CTooltipPlacement.RightStart]: CTooltipPlacement.LeftStart,
  [CTooltipPlacement.RightEnd]: CTooltipPlacement.LeftEnd,
};

const FLIP_THRESHOLD = 60;

function resolvePlacement(preferred: CTooltipPlacement, rect: DOMRect): CTooltipPlacement {
  if (preferred.startsWith('top') && rect.top < FLIP_THRESHOLD) return FLIP_MAP[preferred];
  if (preferred.startsWith('bottom') && window.innerHeight - rect.bottom < FLIP_THRESHOLD) return FLIP_MAP[preferred];
  if (preferred.startsWith('left') && rect.left < FLIP_THRESHOLD) return FLIP_MAP[preferred];
  if (preferred.startsWith('right') && window.innerWidth - rect.right < FLIP_THRESHOLD) return FLIP_MAP[preferred];
  return preferred;
}

function computePosition(resolved: CTooltipPlacement, rect: DOMRect): { top: number; left: number } {
  if (resolved.startsWith('top')) return { top: rect.top, left: rect.left + rect.width / 2 };
  if (resolved.startsWith('bottom')) return { top: rect.bottom, left: rect.left + rect.width / 2 };
  if (resolved.startsWith('left')) return { top: rect.top + rect.height / 2, left: rect.left };
  return { top: rect.top + rect.height / 2, left: rect.right };
}

// --- Singleton context ---

interface TooltipRequest {
  content: React.ReactNode;
  placement: CTooltipPlacement;
  arrow?: boolean;
  rect: DOMRect;
}

interface TooltipContextValue {
  show(request: TooltipRequest): void;
  hide(): void;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

function useTooltipContext(): TooltipContextValue {
  const context = useContext(TooltipContext);
  if (!context) throw new Error('CTooltip requires CTooltipProvider. Add it to your CProvider.');
  return context;
}

// --- Provider (singleton renderer) ---

interface TooltipState {
  visible: boolean;
  content: React.ReactNode;
  top: number;
  left: number;
  resolved: CTooltipPlacement;
  arrow: boolean;
}

const HIDDEN: TooltipState = { visible: false, content: '', top: 0, left: 0, resolved: CTooltipPlacement.Top, arrow: false };

export function CTooltipProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [state, setState] = useState<TooltipState>(HIDDEN);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const show = useCallback((request: TooltipRequest) => {
    clearTimeout(leaveTimer.current);
    const resolved = resolvePlacement(request.placement, request.rect);
    const { top, left } = computePosition(resolved, request.rect);
    setState({ visible: true, content: request.content, top, left, resolved, arrow: request.arrow ?? false });
  }, []);

  const hide = useCallback(() => {
    leaveTimer.current = setTimeout(() => setState(HIDDEN), 100);
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(leaveTimer.current);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hide();
  }, [hide]);

  useEffect(() => {
    if (!state.visible || !tooltipRef.current) return;
    const element = tooltipRef.current;
    const rect = element.getBoundingClientRect();
    const padding = 8;

    if (rect.left < padding || rect.right > window.innerWidth - padding) {
      const tooltipWidth = rect.width;
      let clampedLeft = state.left;

      if (state.resolved.startsWith('top') || state.resolved.startsWith('bottom')) {
        const idealLeft = state.left - tooltipWidth / 2;
        const idealRight = state.left + tooltipWidth / 2;

        if (idealLeft < padding) {
          clampedLeft = padding + tooltipWidth / 2;
        } else if (idealRight > window.innerWidth - padding) {
          clampedLeft = window.innerWidth - padding - tooltipWidth / 2;
        }
      }

      if (clampedLeft !== state.left) {
        element.style.left = `${clampedLeft}px`;
      }
    }
  }, [state.visible, state.left, state.content, state.resolved]);

  const contextValue = useMemo(() => ({ show, hide }), [show, hide]);

  return (
    <TooltipContext.Provider value={contextValue}>
      {children}
      {state.visible && createPortal(
        <div
          ref={tooltipRef}
          className={classNames('c-tooltip', `c-tooltip--${state.resolved}`, { 'c-tooltip--arrow': state.arrow })}
          style={{ top: state.top, left: state.left }}
          role="tooltip"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <span className="c-tooltip__content">{state.content}</span>
          {state.arrow && <span className="c-tooltip__arrow" />}
        </div>,
        document.body,
      )}
    </TooltipContext.Provider>
  );
}

// --- Trigger component (lightweight) ---

export interface CTooltipProps {
  content: React.ReactNode;
  placement?: CTooltipPlacement;
  arrow?: boolean;
  enterDelay?: number;
  disabled?: boolean;
  class?: string;
}

export function CTooltip(props: PropsWithChildren<CTooltipProps>): React.JSX.Element {
  const { show, hide } = useTooltipContext();
  const triggerRef = useRef<HTMLDivElement>(null);
  const enterTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const enterDelay = props.enterDelay ?? 100;
  const placement = props.placement ?? CTooltipPlacement.Top;

  useEffect(() => () => clearTimeout(enterTimer.current), []);

  const handleEnter = useCallback(() => {
    if (props.disabled) return;
    clearTimeout(enterTimer.current);
    enterTimer.current = setTimeout(() => {
      if (!triggerRef.current) return;
      show({
        content: props.content,
        placement,
        arrow: props.arrow,
        rect: triggerRef.current.getBoundingClientRect(),
      });
    }, enterDelay);
  }, [props.disabled, props.content, props.arrow, placement, enterDelay, show]);

  const handleLeave = useCallback(() => {
    clearTimeout(enterTimer.current);
    hide();
  }, [hide]);

  return (
    <div
      ref={triggerRef}
      className="c-tooltip__trigger"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      {props.children}
    </div>
  );
}
